import { useState, useRef, useEffect, type KeyboardEvent } from 'react';
import { useChatStore } from '../../stores/chat';
import { useTasksStore } from '../../stores/tasks';
import { useAuthStore } from '../../stores/auth';
import { getSocket } from '../../lib/socket';
import { apiErrorMessage } from '../../lib/apiError';
import { threadsApi, uploadApi, type AppUser } from '../../lib/api';
import SegmentedControl from '../ui/SegmentedControl';
import RoleChip from '../ui/RoleChip';
import ComposerPlusMenu from './ComposerPlusMenu';
import TeammatePicker from './TeammatePicker';
import { AddTaskModal } from '../tasks/TasksPanel';
import { Plus, Lock, Image, FileText, X, Check, Loader2 } from '../../lib/icons';

interface Props {
  threadId: string;
  isInternal: boolean;
  onIsInternalChange: (v: boolean) => void;
}

export default function MessageInput({ threadId, isInternal, onIsInternalChange }: Props) {
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);
  const [pendingMedia, setPendingMedia] = useState<{
    url: string; filename: string; messageType: 'image' | 'document';
  } | null>(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [plusMenuOpen, setPlusMenuOpen] = useState(false);
  const [pickerMode, setPickerMode] = useState<'add' | 'remove' | null>(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);

  // Use store users (already loaded by AppPage) — avoids separate fetch + timing gap
  const allUsers = useChatStore((s) => s.users);
  const [suggestions, setSuggestions] = useState<AppUser[]>([]);
  const [suggestionIdx, setSuggestionIdx] = useState(0);
  const [mentionStart, setMentionStart] = useState<number | null>(null);
  const [mentionHint, setMentionHint] = useState<string | null>(null);

  const sendMessage  = useChatStore((s) => s.sendMessage);
  const leaveThread  = useChatStore((s) => s.leaveThread);
  const createTasks  = useTasksStore((s) => s.createTasks);
  const threadType         = useChatStore((s) => s.threads.find((t) => t.id === threadId)?.type);
  const threadParticipants = useChatStore((s) => s.threads.find((t) => t.id === threadId)?.participants ?? []);
  const threadContact      = useChatStore((s) => s.threads.find((t) => t.id === threadId)?.contact);
  const threadName         = useChatStore((s) => s.threads.find((t) => t.id === threadId)?.name);
  const user = useAuthStore((s) => s.user);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const toastTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isTypingRef = useRef(false);


  const isInternalThread = threadType === 'internal' || threadType === 'direct' || threadType === 'org';
  const isOrgThread    = threadType === 'org';
  const isClientThread = threadType === 'client';

  useEffect(() => {
    if (isInternalThread) return;
    if (isInternal) return;
    const excluded = ['//add', '//leave', '//task'];
    if (text.startsWith('//') && !excluded.some((c) => text.startsWith(c))) {
      onIsInternalChange(true);
    }
  }, [text, isInternalThread]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const addMatch    = text.match(/(\/\/add\s+@)(\w*)$/i);
    const removeMatch = text.match(/(\/\/remove\s+@)(\w*)$/i);
    const taskMatch   = text.match(/^\/\/task\s+\S.*\s@(\w*)$/i);
    const taskEarlyAt = /^\/\/task\s*@/i.test(text);

    if (addMatch) {
      const q = addMatch[2].toLowerCase();
      setMentionStart(text.length - q.length);
      if (!isClientThread) {
        setMentionHint('//add only works in client threads');
        setSuggestions([]);
      } else {
        setMentionHint(null);
        const existingIds = new Set(threadParticipants.map((p) => p.user_id).filter(Boolean));
        setSuggestions(
          allUsers.filter(u => u.id !== user?.id && !existingIds.has(u.id) && u.name.toLowerCase().includes(q)).slice(0, 6)
        );
        setSuggestionIdx(0);
      }
    } else if (removeMatch) {
      const q = removeMatch[2].toLowerCase();
      setMentionStart(text.length - q.length);
      if (!isClientThread) {
        setMentionHint('//remove only works in client threads');
        setSuggestions([]);
      } else {
        setMentionHint(null);
        setSuggestions(
          threadParticipants
            .filter(p => p.user_id && p.user_id !== user?.id)
            .map(p => allUsers.find(u => u.id === p.user_id))
            .filter((u): u is AppUser => !!u && u.name.toLowerCase().includes(q))
            .slice(0, 6)
        );
        setSuggestionIdx(0);
      }
    } else if (taskMatch) {
      const q = taskMatch[1].toLowerCase();
      setMentionStart(text.length - q.length);
      setMentionHint(null);
      setSuggestions(
        allUsers.filter(u => u.name.toLowerCase().includes(q)).slice(0, 6)
      );
      setSuggestionIdx(0);
    } else if (taskEarlyAt) {
      setSuggestions([]);
      setMentionStart(null);
      setMentionHint('Add a task title first, then @mention assignees');
    } else {
      setSuggestions([]);
      setMentionStart(null);
      setMentionHint(null);
    }
  }, [text, allUsers, user, threadParticipants, threadType, isOrgThread, isClientThread]);

  function showToast(msg: string, ok: boolean) {
    setToast({ msg, ok });
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToast(null), 3500);
  }

  function handleTyping() {
    const socket = getSocket();
    if (!isTypingRef.current) {
      isTypingRef.current = true;
      socket.emit('typing:start', threadId);
    }
    if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    typingTimerRef.current = setTimeout(() => {
      isTypingRef.current = false;
      socket.emit('typing:stop', threadId);
    }, 2500);
  }

  function acceptSuggestion(u: AppUser) {
    if (mentionStart === null) return;
    const slug = u.name.replace(/\s+/g, '');
    const newText = text.slice(0, mentionStart) + slug + ' ';
    setText(newText);
    setSuggestions([]);
    setMentionStart(null);
    textareaRef.current?.focus();
  }

  async function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!fileInputRef.current) return;
    fileInputRef.current.value = '';
    if (!file) return;

    setUploading(true);
    try {
      const { data } = await uploadApi.upload(file);
      setPendingMedia({ url: data.url, filename: data.filename, messageType: data.messageType });
    } catch (err: unknown) {
      const msg = apiErrorMessage(err, 'Upload failed');
      showToast(msg, false);
    } finally {
      setUploading(false);
    }
  }

  async function handleLeaveFromMenu() {
    try {
      await leaveThread(threadId);
    } catch (err: unknown) {
      showToast(apiErrorMessage(err, 'Failed to leave thread'), false);
    }
  }

  async function handleSend() {
    const content = text.trim();
    if ((!content && !pendingMedia) || sending) return;

    const addMatch = content.match(/^\/\/add\s+@(\S+)$/i);
    if (addMatch) {
      if (!isClientThread) {
        showToast('//add only works in client threads', false);
        return;
      }
      const slug = addMatch[1].toLowerCase();
      const existingIds = new Set(threadParticipants.map((p) => p.user_id).filter(Boolean));
      const target = allUsers.find((u) => u.name.replace(/\s+/g, '').toLowerCase() === slug);
      if (!target) {
        showToast(`No user matching "@${addMatch[1]}" found`, false);
        return;
      }
      if (existingIds.has(target.id)) {
        showToast(`${target.name} is already in this thread`, false);
        return;
      }
      setSending(true);
      try {
        await threadsApi.addParticipant(threadId, target.id);
        setText('');
        showToast(`${target.name} has been added to this thread`, true);
      } catch (err: unknown) {
        showToast(apiErrorMessage(err, 'Failed to add participant'), false);
      } finally {
        setSending(false);
      }
      return;
    }

    const removeMatch = content.match(/^\/\/remove\s+@(\S+)$/i);
    if (removeMatch) {
      if (!isClientThread) {
        showToast('//remove only works in client threads', false);
        return;
      }
      const slug = removeMatch[1].toLowerCase();
      const participantUsers = threadParticipants
        .filter(p => p.user_id && p.user_id !== user?.id)
        .map(p => allUsers.find(u => u.id === p.user_id))
        .filter(Boolean) as AppUser[];
      const target = participantUsers.find(u => u.name.replace(/\s+/g, '').toLowerCase() === slug);
      if (!target) {
        showToast(`No participant matching "@${removeMatch[1]}" found`, false);
        return;
      }
      setSending(true);
      try {
        await threadsApi.removeParticipant(threadId, target.id);
        setText('');
        showToast(`${target.name} has been removed from this thread`, true);
      } catch (err: unknown) {
        showToast(apiErrorMessage(err, 'Failed to remove participant'), false);
      } finally {
        setSending(false);
      }
      return;
    }

    if (content === '//leave') {
      setSending(true);
      try {
        await leaveThread(threadId);
        setText('');
      } catch (err: unknown) {
        showToast(apiErrorMessage(err, 'Failed to leave thread'), false);
      } finally {
        setSending(false);
      }
      return;
    }

    const taskMatch = content.match(/^\/\/task\s+(.+)$/i);
    if (taskMatch) {
      const raw = taskMatch[1];
      const dueMatch = raw.match(/\bdue\s+(\d{4}-\d{2}-\d{2})/i);
      const dueAt = dueMatch ? dueMatch[1] : undefined;
      const withoutDue = dueMatch ? raw.replace(dueMatch[0], '').trim() : raw;
      const mentionMatches = [...withoutDue.matchAll(/@(\S+)/g)];
      const mentionSlugs = mentionMatches.map((m) => m[1].toLowerCase());
      const title = withoutDue.replace(/@\S+/g, '').replace(/\bassignee\b/gi, '').trim();

      if (!title) { showToast('Task title is required', false); return; }

      const assignedTo: string[] = mentionSlugs.length === 0
        ? [user!.id]
        : mentionSlugs
            .map((slug) => allUsers.find((u) => u.name.replace(/\s+/g, '').toLowerCase() === slug)?.id)
            .filter(Boolean) as string[];

      if (mentionSlugs.length > 0 && assignedTo.length === 0) {
        showToast(`No users found for: ${mentionSlugs.join(', ')}`, false);
        return;
      }

      setSending(true);
      try {
        const created = await createTasks({ title, assignedTo, threadId, dueAt });
        const names = [...new Set(created.map((t) => t.assignee_name ?? '').filter(Boolean))].join(', ');
        setText('');
        showToast(`Task created for ${names}`, true);
      } catch (err: unknown) {
        showToast(apiErrorMessage(err, 'Failed to create task'), false);
      } finally {
        setSending(false);
      }
      return;
    }

    if (!text.trim() && !pendingMedia) return;

    setSending(true);
    try {
      const finalContent = isInternal && content.startsWith('// ')
        ? content.slice(3)
        : isInternal && content.startsWith('//')
        ? content.slice(2).trim()
        : content;

      await sendMessage(
        finalContent,
        isInternal,
        pendingMedia
          ? { mediaUrl: pendingMedia.url, mediaFilename: pendingMedia.filename, messageType: pendingMedia.messageType }
          : undefined,
      );
      setText('');
      onIsInternalChange(false);
      setPendingMedia(null);
    } catch (err) {
      console.error('Send failed', err);
    } finally {
      setSending(false);
    }
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    if (suggestions.length > 0) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setSuggestionIdx((i) => (i + 1) % suggestions.length); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setSuggestionIdx((i) => (i - 1 + suggestions.length) % suggestions.length); return; }
      if (e.key === 'Tab' || e.key === 'Enter') { e.preventDefault(); acceptSuggestion(suggestions[suggestionIdx]); return; }
      if (e.key === 'Escape') { setSuggestions([]); return; }
    }
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); }
  }

  function handleChange(v: string) {
    setText(v);
    handleTyping();
    const el = textareaRef.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 120) + 'px'; }
  }

  const modeIsInternal = isInternal && isClientThread;
  const clientDisplayName = threadContact?.display_name || threadContact?.name || threadName || '';
  const firstName = clientDisplayName.split(' ')[0];
  const nonSelfParticipantNames = threadParticipants
    .filter((p) => p.user_id && p.user_id !== user?.id)
    .map((p) => p.name ?? p.contact_name ?? '')
    .filter(Boolean);

  return (
    <div style={{ borderTop: '1px solid var(--shell-border)', background: 'var(--vs1)', flexShrink: 0, position: 'relative' }}>

      {/* Mention hint */}
      {mentionHint && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 16, right: 16, marginBottom: 4,
          background: 'var(--vs2)', border: '1px solid var(--note-border)', borderRadius: 4,
          padding: '8px 14px', fontSize: '0.78rem', color: 'var(--note-label)',
          display: 'flex', alignItems: 'center', gap: 8, zIndex: 50,
          boxShadow: '0 4px 12px rgba(0,0,0,.12)',
        }}>
          {mentionHint}
        </div>
      )}

      {/* Mention suggestions popover */}
      {suggestions.length > 0 && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 16, right: 16, marginBottom: 4,
          background: 'var(--vs2)', border: '1px solid var(--vb)', borderRadius: 4,
          boxShadow: '0 4px 16px rgba(0,0,0,.15)', overflow: 'hidden', zIndex: 50,
        }}>
          <div style={{ padding: '4px 10px 2px', fontSize: '0.65rem', color: 'var(--text-muted)', borderBottom: '1px solid var(--vb)' }}>
            ↑↓ navigate · Tab/Enter to select · Esc to dismiss
          </div>
          {suggestions.map((u, i) => (
            <button
              key={u.id}
              onMouseDown={(e) => { e.preventDefault(); acceptSuggestion(u); }}
              onMouseEnter={() => setSuggestionIdx(i)}
              style={{
                display: 'flex', alignItems: 'center', gap: 10, width: '100%',
                padding: '7px 12px', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: i === suggestionIdx ? 'rgba(29,158,117,.08)' : 'transparent',
                borderLeft: i === suggestionIdx ? '3px solid var(--v1)' : '3px solid transparent',
                transition: 'background .1s',
              }}
            >
              <span style={{
                width: 26, height: 26, borderRadius: '50%', flexShrink: 0,
                background: 'var(--navy-800)', color: 'var(--v2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.65rem', fontWeight: 700,
              }}>
                {u.avatarInitials ?? u.name.slice(0, 2).toUpperCase()}
              </span>
              <span style={{ flex: 1 }}>
                <span style={{ color: 'var(--text)', fontSize: '0.82rem', fontWeight: 600 }}>{u.name}</span>
                <span style={{ marginLeft: 6, verticalAlign: 'middle' }}>
                  <RoleChip role={u.role} />
                </span>
              </span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{u.email}</span>
            </button>
          ))}
        </div>
      )}

      {/* Toast notification */}
      {toast && (
        <div style={{
          position: 'absolute', bottom: '100%', left: 16, right: 16, marginBottom: 4,
          padding: '8px 14px', borderRadius: 4, fontSize: '0.8rem', fontWeight: 500,
          display: 'flex', alignItems: 'center', gap: 8,
          background: toast.ok ? 'rgba(29,158,117,.1)' : 'rgba(226,75,74,.1)',
          border: `1px solid ${toast.ok ? 'rgba(29,158,117,.3)' : 'rgba(226,75,74,.3)'}`,
          color: toast.ok ? 'var(--green)' : 'var(--red)',
          zIndex: 50,
        }}>
          {toast.ok ? <Check size={14} strokeWidth={2} /> : <X size={14} strokeWidth={2} />}
          {toast.msg}
        </div>
      )}

      {/* Pending media preview */}
      {pendingMedia && (
        <div style={{
          margin: '0 16px 6px',
          padding: '6px 10px',
          background: 'var(--vs2)',
          border: '1px solid var(--vb)',
          borderRadius: 4,
          display: 'flex', alignItems: 'center', gap: 8,
          fontSize: '0.78rem', color: 'var(--text)',
        }}>
          {pendingMedia.messageType === 'image' ? <Image size={15} strokeWidth={1.8} /> : <FileText size={15} strokeWidth={1.8} />}
          <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {pendingMedia.filename}
          </span>
          <button
            onClick={() => setPendingMedia(null)}
            title="Remove attachment"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: 'var(--xs)', padding: '0 2px', display: 'flex',
            }}
          >
            <X size={14} strokeWidth={1.8} />
          </button>
        </div>
      )}

      {/* Mode row — full-width segmented control (client threads) or a static caption (team-only threads) */}
      <div style={{ padding: '10px 16px 0' }}>
        {isClientThread ? (
          <SegmentedControl
            fullWidth
            tone={modeIsInternal ? 'note' : 'accent'}
            value={modeIsInternal ? 'note' : 'message'}
            onChange={(v) => onIsInternalChange(v === 'note')}
            options={[
              { value: 'message', label: 'Message client' },
              { value: 'note', label: 'Internal note', icon: <Lock size={12} strokeWidth={1.8} /> },
            ]}
          />
        ) : isOrgThread ? (
          <div style={{
            padding: '6px 10px', borderRadius: 6, background: 'rgba(55,138,221,.12)',
            fontSize: '0.72rem', color: 'var(--blue-soft)', fontWeight: 600, textAlign: 'center',
          }}>
            INTERNAL BY DEFAULT — everyone here is a teammate, no client sees this thread
          </div>
        ) : (
          <div style={{ fontSize: '0.72rem', color: 'var(--shell-xs)', textAlign: 'center', padding: '4px 0' }}>
            Everything here is internal — no message/note switch needed.
          </div>
        )}
      </div>

      {/* Input row */}
      <div style={{ padding: '8px 16px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".jpg,.jpeg,.png,.gif,.webp,.pdf,.doc,.docx,.txt"
          style={{ display: 'none' }}
          onChange={handleFileSelect}
        />

        {/* "+" menu button */}
        <button
          onClick={() => setPlusMenuOpen(true)}
          title="Attach a file, add/remove a teammate, create a task, or leave"
          style={{
            background: 'none', border: '1px solid var(--vb)', borderRadius: 6,
            cursor: 'pointer', color: 'var(--xs)', padding: '0 9px', flexShrink: 0,
            alignSelf: 'stretch', display: 'flex', alignItems: 'center',
          }}
        >
          <Plus size={17} strokeWidth={1.8} />
        </button>

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
          placeholder={
            modeIsInternal
              ? 'Internal note…'
              : isClientThread
                ? `Message ${firstName || 'client'}…`
                : 'Message…'
          }
          style={{
            flex: 1, background: 'var(--vs2)',
            border: `1px solid ${modeIsInternal ? 'var(--note-border)' : 'var(--vb)'}`,
            borderRadius: 4, padding: '8px 12px',
            color: modeIsInternal ? 'var(--note-label)' : 'var(--text)',
            fontSize: '0.83rem', resize: 'none', lineHeight: 1.5,
            maxHeight: 120, overflowY: 'auto', transition: 'border-color .15s',
          }}
        />

        <button
          onClick={handleSend}
          disabled={sending || uploading || (!text.trim() && !pendingMedia)}
          style={{
            background: sending || uploading || (!text.trim() && !pendingMedia) ? 'var(--vb2)' : 'var(--v1)',
            color: sending || uploading || (!text.trim() && !pendingMedia) ? 'var(--slate-500)' : 'var(--text-on-accent)',
            border: 'none', borderRadius: 4,
            padding: '0 16px', cursor: sending || uploading || (!text.trim() && !pendingMedia) ? 'not-allowed' : 'pointer',
            fontSize: '0.8rem', fontWeight: 700, flexShrink: 0, transition: 'background .15s',
            alignSelf: 'stretch', display: 'flex', alignItems: 'center', gap: 6,
          }}
        >
          {uploading && <Loader2 size={13} strokeWidth={2} style={{ animation: 'rt-spin .8s linear infinite' }} />}
          {sending ? '…' : 'Send'}
        </button>
      </div>

      {plusMenuOpen && (
        <ComposerPlusMenu
          isClientThread={isClientThread}
          isOrgThread={isOrgThread}
          participantNames={nonSelfParticipantNames}
          onAttach={() => fileInputRef.current?.click()}
          onAddTeammate={() => setPickerMode('add')}
          onRemoveTeammate={() => setPickerMode('remove')}
          onCreateTask={() => setTaskModalOpen(true)}
          onLeave={() => void handleLeaveFromMenu()}
          onClose={() => setPlusMenuOpen(false)}
        />
      )}

      {pickerMode && (
        <TeammatePicker threadId={threadId} mode={pickerMode} onClose={() => setPickerMode(null)} />
      )}

      {taskModalOpen && (
        <AddTaskModal threadId={threadId} onClose={() => setTaskModalOpen(false)} />
      )}
    </div>
  );
}
